import { FormField } from '@/components/templates/entity-form'
import { DatePicker } from '@/components/ui/date-time-picker'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { apiFieldMessages } from '@/lib/contacts/errors'
import { enrolmentStatuses, type Enrolment, type EnrolmentStatus } from './enrolment'

export interface EnrolmentValues {
  status: EnrolmentStatus
  enrolled_on: string
  completed_on: string
  withdrawn_on: string
  score: string
  grade_label: string
  credits_earned: string
}

export function initialEnrolmentValues(row?: Enrolment): EnrolmentValues {
  const value = (key: keyof EnrolmentValues) => row?.[key] ?? ''
  return {
    status: row?.status ?? 'enrolled',
    enrolled_on: value('enrolled_on'),
    completed_on: value('completed_on'),
    withdrawn_on: value('withdrawn_on'),
    score: value('score'),
    grade_label: value('grade_label'),
    credits_earned: value('credits_earned'),
  }
}

export function EnrolmentFields({
  editing,
  unit,
  values,
  update,
  error,
}: {
  editing: boolean
  unit: string
  values: EnrolmentValues
  update: (patch: Partial<EnrolmentValues>) => void
  error: Error | null
}) {
  const date = (name: 'enrolled_on' | 'completed_on' | 'withdrawn_on', label: string) => (
    <FormField
      label={label}
      htmlFor={`enrolment-${name}`}
      error={apiFieldMessages(error, name).join(' ')}
    >
      <DatePicker
        id={`enrolment-${name}`}
        label={label}
        value={values[name]}
        onChange={(value) => update({ [name]: value })}
      />
    </FormField>
  )
  return (
    <>
      {editing && (
        <FormField
          label="Status"
          htmlFor="enrolment-status"
          error={apiFieldMessages(error, 'status').join(' ')}
        >
          <Select
            value={values.status}
            onValueChange={(status: EnrolmentStatus) => update({ status })}
          >
            <SelectTrigger id="enrolment-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(enrolmentStatuses).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
      )}
      {date('enrolled_on', 'Enrolled on')}
      {editing && (
        <>
          {(values.status === 'completed' || values.status === 'failed') &&
            date('completed_on', 'Completed on')}
          {values.status === 'withdrawn' && date('withdrawn_on', 'Withdrawn on')}
          <EnrolmentResultFields unit={unit} values={values} update={update} error={error} />
        </>
      )}
    </>
  )
}

function EnrolmentResultFields({
  unit,
  values,
  update,
  error,
}: {
  unit: string
  values: EnrolmentValues
  update: (patch: Partial<EnrolmentValues>) => void
  error: Error | null
}) {
  const fields = [
    { name: 'score', label: 'Score' },
    { name: 'grade_label', label: 'Grade label' },
    { name: 'credits_earned', label: `${unit} earned` },
  ] as const
  return (
    <>
      {fields
        .filter(({ name }) => name !== 'credits_earned' || values.status === 'completed')
        .map(({ name, label }) => (
          <FormField
            key={name}
            label={label}
            htmlFor={`enrolment-${name}`}
            error={apiFieldMessages(error, name).join(' ')}
          >
            <Input
              id={`enrolment-${name}`}
              type={name === 'grade_label' ? 'text' : 'number'}
              step="any"
              value={values[name]}
              onChange={(event) => update({ [name]: event.target.value })}
            />
          </FormField>
        ))}
    </>
  )
}
